import { t } from "@/i18n";
import { AlertTriangle, Info, Loader2, Inbox } from "lucide-react";

export const DemoBadge = ({ className = "", label }) => (
  <span data-testid="demo-badge" className={`inline-flex items-center rounded-md border border-[#3a3a3a] bg-[#1b1b1b] px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-[#a3a3a3] ${className}`}>
    {label || t.common.demo}
  </span>
);

export const StatusPill = ({ status, map, tone = {} }) => {
  const colors = { draft: "#6b6b6b", review: "#f5b544", approved: "#8fcb8b", blocked: "#e5655a", pending: "#a3a3a3", running: "#f5b544", done: "#8fcb8b", failed: "#e5655a", cancelled: "#6b6b6b", ...tone };
  return (
    <span data-testid={`status-pill-${status}`} className="inline-flex items-center gap-1.5 rounded-full border border-[#303030] bg-[#151515] px-2 py-0.5 text-[11px] font-medium text-[#d8d4cd]">
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: colors[status] || "#6b6b6b" }} />
      {map?.[status] || status}
    </span>
  );
};

export const EmptyState = ({ title, body, action, icon: Icon = Inbox, testId = "empty-state" }) => (
  <div data-testid={testId} className="flex h-full min-h-[240px] flex-col items-center justify-center rounded-xl border border-dashed border-[#303030] p-10 text-center">
    <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl border border-[#303030] bg-[#1b1b1b] text-[#a3a3a3]">
      <Icon size={20} />
    </div>
    <h3 className="text-base font-semibold">{title}</h3>
    {body && <p className="mt-1 max-w-sm text-sm text-[#a3a3a3]">{body}</p>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

export const LoadingState = ({ label = t.common.loading, testId = "loading-state" }) => (
  <div data-testid={testId} className="flex h-full min-h-[200px] items-center justify-center gap-2 text-sm text-[#a3a3a3]">
    <Loader2 size={16} className="animate-spin" /> {label}
  </div>
);

export const ErrorState = ({ title, body, action, testId = "error-state" }) => (
  <div data-testid={testId} className="flex min-h-[200px] flex-col items-center justify-center rounded-xl border border-[#4a2a27] bg-[#1c1414] p-8 text-center">
    <AlertTriangle size={20} className="mb-3 text-[#e5655a]" />
    <h3 className="text-base font-semibold">{title}</h3>
    {body && <p className="mt-1 max-w-sm text-sm text-[#a3a3a3]">{body}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

export const Notice = ({ children, tone = "info", testId = "notice", className = "" }) => {
  const styles = tone === "warn" ? "border-[#4d3a14] bg-[#1e1808] text-[#f1d9a5]" : "border-[#303030] bg-[#161616] text-[#a3a3a3]";
  return (
    <div data-testid={testId} className={`flex items-start gap-2.5 rounded-lg border px-3 py-2.5 text-[13px] leading-relaxed ${styles} ${className}`}>
      {tone === "warn" ? <AlertTriangle size={15} className="mt-0.5 shrink-0" /> : <Info size={15} className="mt-0.5 shrink-0" />}
      <div>{children}</div>
    </div>
  );
};

export const SectionTitle = ({ eyebrow, title, right }) => (
  <div className="mb-4 flex items-end justify-between gap-4">
    <div>
      {eyebrow && <div className="v-eyebrow mb-1">{eyebrow}</div>}
      <h2 className="text-xl font-semibold">{title}</h2>
    </div>
    {right}
  </div>
);
